import { dryrun } from "./ao-connection"
import { supabase } from "./supabase"
import type { PoolOverviewRaw, PoolReserves, Tag } from "../types"
import ENV from "@/env"
import { getHBBaseUrl } from "@/utils/hb-router"

export type BasePool = {
  id: string
  baseToken: string
  quoteToken: string
  created: number
  verified?: boolean
  status?: "private" | "public"
}

export type Pool = PoolOverviewRaw & BasePool

export async function getAllPools(): Promise<Pool[]> {
  const { data, error } = await supabase.functions.invoke("pools", {
    body: {},
  })

  if (error) {
    throw new Error(`Supabase function error: ${error.message}`)
  }

  if (!data) {
    throw new Error("No response from Get-Pools")
  }

  try {
    let pools: PoolOverviewRaw[] = data
    const formattedPools: Pool[] = []

    // Exclude pools with id = ["XX", "YY", "ZZ"]
    const excludedPools = [
      "zN1qKpTdfVS-Y6qK7UiguIsZ_XdIsRw3cYbw-73GnC4",
      "nHWcfNzeqjZ8Bot4R-xs_7Jez7Fl2AeDzKlN7qWmS24",
      "4IGzddk67tIZ9zS6AEGO4DA_hq5-LONepeWd3xqt5sM",
    ]

    pools = pools.filter((pool) => !excludedPools.includes(pool.amm_process))

    for (const pool of pools) {
      formattedPools.push({
        ...pool,
        created: pool.amm_discovered_at_ts ?? 0,
        id: pool.amm_process,
        baseToken: pool.token1,
        quoteToken: pool.token0,
        status: pool.amm_status || "private",
        pool_fee_bps: pool.pool_fee_bps,
      })
    }

    return formattedPools
  } catch (error) {
    throw new Error("Response malformed")
  }
}

export async function getPoolByAddress(address: string): Promise<Pool[]> {
  const { data, error } = await supabase.functions.invoke("pools", {
    body: {
      pool_id: address,
    },
  })

  if (error) {
    throw new Error(`Supabase function error: ${error.message}`)
  }

  if (!data) {
    throw new Error("No response from Get-Pools")
  }

  try {
    const pools: PoolOverviewRaw[] = data
    const formattedPools: Pool[] = []

    for (const pool of pools) {
      formattedPools.push({
        ...pool,
        created: pool.amm_discovered_at_ts ?? 0,
        id: pool.amm_process,
        baseToken: pool.token1,
        quoteToken: pool.token0,
        status: pool.amm_status || "private",
      })
    }

    return formattedPools
  } catch (error) {
    throw new Error("Response malformed")
  }
}

export async function getPoolById(poolId: string): Promise<Pool> {
  return (await getPoolByAddress(poolId).then((pools) =>
    pools.find((x) => x.id === poolId),
  )) as Pool
}

export async function getOnchainPoolReservesHB(
  pool: string,
  pair: [string, string],
): Promise<PoolReserves> {
  const HB_API = getHBBaseUrl(pool)

  const url = `${HB_API}/${pool}~process@1.0/compute/reserves`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 2000)

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    })
    clearTimeout(timeoutId)

    if (!res.ok) {
      throw new Error(`Error fetching reserves from HB API: ${res.statusText}`)
    }

    const data = await res.json()
    const aReserves = data[pair[0]]
    const bReserves = data[pair[1]]

    if (!aReserves || !bReserves) {
      throw new Error(`Invalid reserves data from HB API: ${JSON.stringify(data)}`)
    }

    return {
      baseToken: Number.parseFloat(aReserves),
      quoteToken: Number.parseFloat(bReserves),
      baseTokenAsString: aReserves,
      quoteTokenAsString: bReserves,
    }
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out after 5 seconds")
    }
    throw error
  }
}

export async function getOnchainPoolReservesDryRun(pool: BasePool): Promise<PoolReserves> {
  const result = await dryrun({
    process: pool.id,
    tags: [{ name: "Action", value: "Get-Reserves" }],
  })

  if (result.Messages.length === 0)
    throw new Error(`No response from Get-Reserves (pool: ${pool.id})`)
  const tags = result.Messages[0].Tags as Tag[]

  const baseToken = tags.find((x) => x.name === pool.baseToken)?.value
  const quoteToken = tags.find((x) => x.name === pool.quoteToken)?.value

  if (!baseToken || !quoteToken) throw new Error("Response malformed")

  return {
    baseToken: Number.parseFloat(baseToken),
    quoteToken: Number.parseFloat(quoteToken),
    baseTokenAsString: baseToken,
    quoteTokenAsString: quoteToken,
  }
}

export async function getOnchainPoolReserves(pool: BasePool): Promise<PoolReserves> {
  try {
    const dd = await getOnchainPoolReservesHB(pool.id, [pool.baseToken, pool.quoteToken])
    return dd
  } catch (error) {
    const dfl = await getOnchainPoolReservesDryRun(pool)
    return dfl
  }
}

export async function getOnchainPoolFeePercentageHB(poolId: string): Promise<number> {
  const HB_API = getHBBaseUrl(poolId)

  const url = `${HB_API}/${poolId}~process@1.0/compute/general`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 5000)

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    })
    clearTimeout(timeoutId)

    if (!res.ok) {
      throw new Error(`Error fetching fee percentage from HB API: ${res.statusText}`)
    }

    const data = await res.json()
    const feePercentage = data.FeeBps

    if (typeof feePercentage !== "number") {
      throw new Error(`Invalid fee percentage data from HB API: ${JSON.stringify(data)}`)
    }

    return feePercentage
  } catch (error) {
    clearTimeout(timeoutId)
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out after 5 seconds")
    }
    throw error
  }
}

export async function getOnchainPoolFeePercentagDryRun(poolId: string): Promise<number> {
  const result = await dryrun({
    process: poolId,
    tags: [{ name: "Action", value: "Get-Fee-Percentage" }],
  })

  if (result.Messages.length === 0)
    throw new Error(`No response from Get-Fee-Percentage (pool: ${poolId})`)

  const tags = result.Messages[0].Tags as Tag[]
  const tag = tags.find((x) => x.name === "Fee-Percentage")
  const value = Number.parseFloat(tag ? tag.value : "")

  if (Number.isNaN(value)) throw new Error("Response malformed")

  return value
}

export async function getOnchainPoolFeePercentage(poolId: string): Promise<number> {
  try {
    return getOnchainPoolFeePercentageHB(poolId)
  } catch (error) {
    return getOnchainPoolFeePercentagDryRun(poolId)
  }
}

// dryrun({ process: poolId, tags: [{ name: "Action", value: "Get-Price" }] }),
